import React, { useContext } from 'react';
import { Tooltip } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MUIDataTable, {
  MUIDataTableColumnOptions,
  MUIDataTableMeta,
  SelectableRows
} from 'mui-datatables';
import * as R from 'ramda';
import { CaseCalculationPart } from '../../../interfaces/Case';
import { IReferential } from '../../../interfaces/IReferential';
import { Organization } from '../../../interfaces/Organization';
import { displayAmount } from '../utils/calculation.utils';
import { MUITranslations } from '../../../helpers/MUITableTranslations';
import { ApplicationContext } from '../../../context/Contexts';
import { useAppSelector } from '../../../store/configureStore';
import { useTranslate } from '../../../hooks/useTranslate';

export interface AlternativeCalculationOrderedPartsProps {
  parts: CaseCalculationPart[];
  supplierList: Organization[];
  partQualityTypes: IReferential[];
  currency: IReferential | null;
}

// mui-datatables' MUIDataTableMeta.rowData is an untyped any[] of raw per-column cell values.
type TableRowData = (string | number | null | undefined)[];

const AlternativeCalculationOrderedParts: React.FC<AlternativeCalculationOrderedPartsProps> = ({
  parts,
  supplierList,
  partQualityTypes,
  currency
}) => {
  const t = useTranslate();
  const translatorService = useContext(ApplicationContext).translatorService;
  const country = useAppSelector((s) => s.app?.appUser?.organization?.country ?? null);
  const ordered = parts.filter((item) => item.calculationSectionId === 1);

  const displaySupplier = (supplierId: number) =>
    supplierList.find((item) => item.id === supplierId)?.name ?? '';

  const displayPrice = (value: number, partId: number): number => {
    const part = ordered.find((item) => item.id === partId);
    return (part!.alternativePartQualityId === null ? value : part!.alternativePrice)!;
  };

  const displayDiscount = (value: number, partId: number) => {
    const part = ordered.find((item) => item.id === partId);
    return part!.alternativePartQualityId === null
      ? R.isNil(value)
        ? 0
        : value
      : R.isNil(part!.alternativePartDiscount)
      ? 0
      : part!.alternativePartDiscount;
  };

  const displayTotalPrice = (row: TableRowData) => {
    const part = ordered.find((item) => item.id === row[0])!;
    const unitPrice =
      part.alternativePartQualityId === null ? part.originalPrice : part.alternativePrice!;
    const discount =
      part.alternativePartQualityId === null ? part.discount : part.alternativePartDiscount;
    const totalPrice = unitPrice * part.pieceNr;
    return R.isNil(discount) ? totalPrice : totalPrice * (1 - discount / 100);
  };

  const displayQuality = (value: IReferential, row: TableRowData) => {
    const part = ordered.find((item) => item.id === row[0]);
    if (part!.alternativePartQualityId === null) {
      return (
        <Tooltip
          title={
            <div>
              <div>
                {t('CALCULATION_DETAILS_PRODUCER_HEADER', 'Producator') +
                  ': ' +
                  (R.isNil(row[6]) ? '' : row[6])}
              </div>
              <div>
                {t('CALCULATION_DETAILS_DELIVERY_TIME_HEADER', 'Timp livrare') +
                  ': ' +
                  (R.isNil(row[7]) ? '' : row[7])}
              </div>
            </div>
          }
        >
          <div>
            <span>{value.displayName + ' '}</span>
            <FontAwesomeIcon icon={['fas', 'info-circle']} />
          </div>
        </Tooltip>
      );
    }
    const partQualityType = partQualityTypes.find((q) => q.id === part!.alternativePartQualityId);
    return (
      <Tooltip
        title={
          <div>
            <div>
              {t('CALCULATION_DETAILS_ALT_PART_GN_LABEL', 'Cod alternativa') +
                ': ' +
                (part!.alternativeGuideNumber ?? '')}
            </div>
            <div>
              {t('CALCULATION_DETAILS_ALT_PART_NAME_LABEL', 'Descriere alternativa') +
                ': ' +
                (part!.alternativePartName ?? '')}
            </div>
            <div>
              {t('CALCULATION_DETAILS_PRODUCER_HEADER', 'Producator') +
                ': ' +
                (part!.producer ?? '')}
            </div>
            <div>
              {t('CALCULATION_DETAILS_DELIVERY_TIME_HEADER', 'Timp livrare') +
                ': ' +
                (part!.deliveryTime ?? '')}
            </div>
            <div>
              {t('CALCULATION_DETAILS_ALT_PART_QUANTITY_LABEL', 'Cantitate disponibila') +
                ': ' +
                (R.isNil(part!.availableQuantity) ? '' : part!.availableQuantity)}
            </div>
            <div>
              {t(
                'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_STOCK_LOCATION_LABEL',
                'Locatie'
              ) +
                ': ' +
                (part!.alternativePartStockLocation ?? '')}
            </div>
            <div>
              {t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_REMARKS_LABEL', 'Observatii') +
                ': ' +
                (part!.alternativePartRemarks ?? '')}
            </div>
          </div>
        }
      >
        <div>
          <span>{(partQualityType?.displayName ?? '') + ' '}</span>
          <FontAwesomeIcon icon={['fas', 'info-circle']} />
        </div>
      </Tooltip>
    );
  };

  const columns = [
    { name: 'id', options: { display: 'excluded', filter: false } as MUIDataTableColumnOptions },
    {
      name: 'supplierId',
      options: { display: 'excluded', filter: false } as MUIDataTableColumnOptions
    },
    {
      name: 'guideNumber',
      label: t('CALCULATION_DETAILS_GUIDE_NUMBER_HEADER', 'Cod')
    },
    {
      name: 'alternativeGuideNumber',
      label: t('CALCULATION_DETAILS_ALT_GUIDE_NUMBER_HEADER', 'Cod Aftermarket'),
      options: { filter: false } as MUIDataTableColumnOptions
    },
    {
      name: 'name',
      label: t('CALCULATION_DETAILS_DESCRIPTION_HEADER', 'Descriere'),
      options: {
        customBodyRender: (value: string, tableMeta: MUIDataTableMeta) => {
          const item = ordered.find((p) => p.id === tableMeta.rowData[0]);
          if (R.isNil(item)) {
            return '';
          }
          return R.isNil(item.alternativePartQualityId) ? item.name : item.alternativePartName;
        }
      } as MUIDataTableColumnOptions
    },
    {
      name: '',
      label: t('CALCULATION_DETAILS_SUPPLIER_HEADER', 'Furnizor'),
      options: {
        filter: false,
        customBodyRender: (_value: any, tableMeta: MUIDataTableMeta) =>
          displaySupplier(tableMeta.rowData[1])
      } as MUIDataTableColumnOptions
    },
    {
      name: 'producer',
      label: t('CALCULATION_DETAILS_PRODUCER_HEADER', 'Producator'),
      options: { filter: false } as MUIDataTableColumnOptions
    },
    {
      name: 'deliveryTime',
      options: { display: 'excluded', filter: false } as MUIDataTableColumnOptions
    },
    {
      name: 'alternativePartQuality',
      options: { display: 'excluded', filter: false } as MUIDataTableColumnOptions
    },
    {
      name: 'carPartCategory',
      label: t('CALCULATION_DETAILS_QUALITY_HEADER', 'Calitate'),
      options: {
        customBodyRender: (value: IReferential, tableMeta: MUIDataTableMeta) =>
          displayQuality(value, tableMeta.rowData)
      } as MUIDataTableColumnOptions
    },
    {
      name: 'pieceNr',
      label: t('CALCULATION_DETAILS_UNITS_HEADER', 'Unitati')
    },
    {
      name: 'alternativePrice',
      options: { display: 'excluded', filter: false } as MUIDataTableColumnOptions
    },
    {
      name: 'originalPrice',
      label: t('CALCULATION_DETAILS_UNIT_PRICE_HEADER', 'Pret Unitar'),
      options: {
        customBodyRender: (value: number, tableMeta: MUIDataTableMeta) =>
          displayAmount(displayPrice(value, tableMeta.rowData[0]), country)
      } as MUIDataTableColumnOptions
    },
    {
      name: 'discount',
      label: t('CALCULATION_DETAILS_DISCOUNT_HEADER', 'Discount (%)'),
      options: {
        customBodyRender: (value: number, tableMeta: MUIDataTableMeta) =>
          displayDiscount(value, tableMeta.rowData[0])
      } as MUIDataTableColumnOptions
    },
    {
      name: '',
      label: t('CALCULATION_DETAILS_TOTAL_PRICE_HEADER', 'Pret Total'),
      options: {
        customBodyRender: (_value: number, tableMeta: MUIDataTableMeta) =>
          `${displayAmount(displayTotalPrice(tableMeta.rowData), country)} ${
            currency?.displayName ?? ''
          }`.trim()
      } as MUIDataTableColumnOptions
    }
  ];

  const options = {
    filter: false,
    search: false,
    viewColumns: false,
    selectableRows: 'none' as SelectableRows,
    selectableRowsOnClick: false,
    rowHover: false,
    print: false,
    download: false,
    textLabels: MUITranslations.GetTranslations(translatorService),
    sort: false,
    elevation: 1
  };

  return (
    <div className="mt-3">
      <MUIDataTable
        columns={columns}
        data={ordered}
        title={t('ORDERS_ORDERED_PARTS_TITLE', 'Piese comandate')}
        options={options}
      />
    </div>
  );
};

export default AlternativeCalculationOrderedParts;
